import { useEffect, useMemo, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox, NumericTextBox } from "@progress/kendo-react-inputs";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import {
  DashboardCustomization as DashboardPrefs,
  useDashboardPreferences,
  useDashboardPreferencesCatalog,
  useResetDashboardPreferences,
  useUpdateDashboardPreferences,
} from "src/dm/hooks/useDashboardPreferences";
import { useCatMgrs } from "src/dm/hooks/useLookups";

// ---------------------------------------------------------------------------
// Per-user dashboard customization editor.
//
// Surface mirrors the legacy DashboardCustomization.aspx (CustomGridEdit
// control) + the Default.aspx View/Pagination dropdowns:
//   - Visible columns (checkbox list, ordered = legacy display order)
//   - Default status filter (multi-select)
//   - Default banner-code filter (text input — banner lookup is TBD;
//     legacy used a server-side dropdown sourced from LookupBanner)
//   - Default cat-mgr-code scope (multi-select; data from useCatMgrs)
//   - Page size (10 / 25 / 50 / 100 — legacy used a hidden 100 default
//     toggled by the PAGINATE_DFLT_FG flag)
//   - Default sort column + direction (legacy: DefaultSortColumnIndex +
//     DefaultSortAscending in the CustomGrid XML)
//
// Save persists via PUT; Reset clears (the next GET falls back to the
// server-computed defaults). The current user is implicit — IUserContext
// on the server keys off the request principal / impersonation header.
// ---------------------------------------------------------------------------

const card: React.CSSProperties = {
  background: "#fff",
  padding: "1rem 1.25rem",
  borderRadius: "8px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

const sectionTitle: React.CSSProperties = {
  fontSize: "0.85rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#475569",
  marginBottom: "0.5rem",
};

const labelText: React.CSSProperties = {
  display: "block",
  fontSize: "0.8rem",
  color: "#475569",
  marginBottom: "0.25rem",
};

const DashboardCustomizationPage = () => {
  const { data: prefsResponse, isLoading: prefsLoading, isError: prefsError, error: prefsErr } =
    useDashboardPreferences();
  const {
    data: catalog,
    isLoading: catalogLoading,
    isError: catalogError,
    error: catalogErr,
  } = useDashboardPreferencesCatalog();
  const { data: catMgrs } = useCatMgrs();
  const save = useUpdateDashboardPreferences();
  const reset = useResetDashboardPreferences();

  const [draft, setDraft] = useState<DashboardPrefs | null>(null);
  const [saveBanner, setSaveBanner] = useState<string | null>(null);

  useEffect(() => {
    if (prefsResponse?.customization) {
      setDraft({ ...prefsResponse.customization });
    }
  }, [prefsResponse]);

  // CatMgr code-rows for the MultiSelect — Kendo wants objects with a
  // dataItemKey + textField, not raw numbers.
  const catMgrOptions = useMemo(
    () =>
      (catMgrs ?? []).map((c) => ({
        code: Number(c.code),
        label: `${c.code} — ${c.name}`,
      })),
    [catMgrs]
  );

  const selectedCatMgrObjects = useMemo(() => {
    if (!draft) return [];
    const set = new Set(draft.defaultCategoryManagerCodes);
    return catMgrOptions.filter((o) => set.has(o.code));
  }, [draft, catMgrOptions]);

  if (prefsLoading || catalogLoading) return <p>Loading dashboard preferences…</p>;
  // audit/21-visual-diff.md §11 BLOCKER #2: the page used to silently
  // `return null` when the catalog query failed (no catalogError branch),
  // leaving the user staring at a blank page after the loading line
  // vanished. We now surface BOTH error sources, and once everything
  // resolves but draft/catalog are still null we render a "nothing
  // loaded" placeholder rather than rendering empty.
  if (prefsError || catalogError) {
    const err = prefsError ? prefsErr : catalogErr;
    return (
      <div>
        <h1 style={{ marginTop: 0 }}>Customize Dashboard</h1>
        <p style={{ color: "#b91c1c" }}>
          {err instanceof Error ? err.message : "Failed to load preferences"}
        </p>
      </div>
    );
  }
  if (!draft || !catalog) {
    return (
      <div>
        <h1 style={{ marginTop: 0 }}>Customize Dashboard</h1>
        <p style={{ color: "#64748b" }}>
          Preferences and catalog finished loading but came back empty. Try
          reloading the page; if it persists, contact Deal Central support.
        </p>
      </div>
    );
  }

  const setField = <K extends keyof DashboardPrefs>(key: K, value: DashboardPrefs[K]) =>
    setDraft((p) => (p ? { ...p, [key]: value } : p));

  const toggleColumn = (dataField: string, checked: boolean) => {
    setDraft((p) => {
      if (!p) return p;
      const set = new Set(p.visibleColumns);
      if (checked) set.add(dataField);
      else set.delete(dataField);
      // Preserve catalog ordering so the saved list always renders top-to-bottom
      // in the legacy column order.
      const ordered = catalog.columns
        .filter((c) => set.has(c.dataField))
        .map((c) => c.dataField);
      return { ...p, visibleColumns: ordered };
    });
  };

  const handleSave = () => {
    setSaveBanner(null);
    save.mutate(draft, {
      onSuccess: () => setSaveBanner("Preferences saved."),
    });
  };

  const handleReset = () => {
    setSaveBanner(null);
    reset.mutate(undefined, {
      onSuccess: () => setSaveBanner("Reset to defaults."),
    });
  };

  return (
    <div style={{ display: "grid", gap: "1rem", maxWidth: "1100px" }}>
      <div>
        <h1 style={{ marginTop: 0, marginBottom: "0.25rem" }}>Customize Dashboard</h1>
        <p style={{ color: "#64748b", marginTop: 0 }}>
          Choose which columns appear on the dashboard grid and set the default
          filters / sort / page size you want every time you land on it.
        </p>
        {prefsResponse && (
          <p style={{ color: "#94a3b8", fontSize: "0.8rem", marginTop: "0.25rem" }}>
            Scoped to <code>{prefsResponse.qualifiedUserId}</code> —{" "}
            {prefsResponse.isCustomized ? "customized" : "using defaults"}.
          </p>
        )}
      </div>

      {/* --- Columns ------------------------------------------------------ */}
      <section style={card}>
        <div style={sectionTitle}>Visible columns</div>
        <p style={{ color: "#64748b", marginTop: 0, fontSize: "0.85rem" }}>
          Columns appear in the order shown below.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: "0.4rem 1rem",
          }}
        >
          {catalog.columns.map((col) => (
            <Checkbox
              key={col.dataField}
              label={col.headerText}
              checked={draft.visibleColumns.includes(col.dataField)}
              onChange={(e) => toggleColumn(col.dataField, !!e.value)}
            />
          ))}
        </div>
      </section>

      {/* --- Default filters --------------------------------------------- */}
      <section style={card}>
        <div style={sectionTitle}>Default filters</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "1rem",
          }}
        >
          <div>
            <span style={labelText}>Status</span>
            <MultiSelect
              data={catalog.statuses}
              value={draft.defaultStatusFilter}
              onChange={(e) => setField("defaultStatusFilter", (e.value as string[]) ?? [])}
              placeholder="(any status)"
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <span style={labelText}>Banner</span>
            <DropDownList
              data={["", "GE", "MD", "GET-GO", "RX", "OK"]}
              value={draft.defaultBannerCode ?? ""}
              onChange={(e) =>
                setField("defaultBannerCode", (e.value as string) || null)
              }
              defaultItem=""
              style={{ width: "100%" }}
            />
            <p style={{ fontSize: "0.75rem", color: "#94a3b8", margin: "0.25rem 0 0" }}>
              Empty = no banner filter (admin / all-banner users).
            </p>
          </div>
          <div>
            <span style={labelText}>Category-manager scope</span>
            <MultiSelect
              data={catMgrOptions}
              textField="label"
              dataItemKey="code"
              value={selectedCatMgrObjects}
              onChange={(e) => {
                const selected = (e.value as Array<{ code: number }>) ?? [];
                setField(
                  "defaultCategoryManagerCodes",
                  selected.map((s) => s.code)
                );
              }}
              placeholder="(no scope — see all)"
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </section>

      {/* --- Sort + page size -------------------------------------------- */}
      <section style={card}>
        <div style={sectionTitle}>Default sort &amp; page size</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "1rem",
          }}
        >
          <div>
            <span style={labelText}>Sort column</span>
            <DropDownList
              data={catalog.columns}
              textField="headerText"
              dataItemKey="dataField"
              value={
                catalog.columns.find((c) => c.dataField === draft.sortColumn) ??
                catalog.columns[0]
              }
              onChange={(e) => setField("sortColumn", e.value.dataField)}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <span style={labelText}>Sort direction</span>
            <DropDownList
              data={[
                { label: "Ascending", value: true },
                { label: "Descending", value: false },
              ]}
              textField="label"
              dataItemKey="value"
              value={
                draft.sortAscending
                  ? { label: "Ascending", value: true }
                  : { label: "Descending", value: false }
              }
              onChange={(e) => setField("sortAscending", e.value.value)}
              style={{ width: "100%" }}
            />
          </div>
          <div>
            <span style={labelText}>Page size</span>
            <NumericTextBox
              value={draft.pageSize}
              onChange={(e) =>
                setField("pageSize", validPageSize(e.value ?? 25))
              }
              min={10}
              max={100}
              step={5}
            />
            <p style={{ fontSize: "0.75rem", color: "#94a3b8", margin: "0.25rem 0 0" }}>
              Snaps to 10 / 25 / 50 / 100 on save.
            </p>
          </div>
        </div>
      </section>

      {/* --- Actions ---------------------------------------------------- */}
      <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", alignItems: "center" }}>
        {saveBanner && (
          <span style={{ color: "#15803d", fontSize: "0.85rem" }}>{saveBanner}</span>
        )}
        {save.isError && (
          <span style={{ color: "#b91c1c", fontSize: "0.85rem" }}>
            {save.error instanceof Error ? save.error.message : "Save failed"}
          </span>
        )}
        <Button onClick={handleReset} disabled={reset.isPending || save.isPending}>
          {reset.isPending ? "Resetting…" : "Reset to defaults"}
        </Button>
        <Button
          themeColor="primary"
          onClick={handleSave}
          disabled={save.isPending || reset.isPending}
        >
          {save.isPending ? "Saving…" : "Save preferences"}
        </Button>
      </div>
    </div>
  );
};

const ALLOWED_PAGE_SIZES = [10, 25, 50, 100];
const validPageSize = (raw: number): number => {
  // Snap to nearest legal value.
  let nearest = ALLOWED_PAGE_SIZES[0];
  let bestDelta = Math.abs(raw - nearest);
  for (const candidate of ALLOWED_PAGE_SIZES) {
    const delta = Math.abs(raw - candidate);
    if (delta < bestDelta) {
      nearest = candidate;
      bestDelta = delta;
    }
  }
  return nearest;
};

export default DashboardCustomizationPage;
